<template>
  <FullCalendar
    id="calendar"
    v-if="appointmentCapacities"
    :options="calendarOptions"
    ref="fullCalendar"
  >
    <template #eventContent="arg">
      <div class="event-content">
        <div class="event-first-line">
          <b-badge
            class="event-badge-type"
            :variant="getAppointmentTypeColor(arg.event.extendedProps.appointmentType)"
          >
            {{ $t(arg.event.extendedProps.appointmentType) }}
          </b-badge>
          <b-badge
            v-if="arg.event.extendedProps.appointment"
            class="event-badge-state"
            :variant="
              getAppointmentStateColor(arg.event.extendedProps.appointment.appointmentState)
            "
          >
            {{ $t(arg.event.extendedProps.appointment.appointmentState) }}
          </b-badge>
          <b-badge
            v-if="arg.event.extendedProps.appointment"
            class="event-badge-approved"
            :variant="
              getYesNoStateColor(arg.event.extendedProps.appointment.appointmentApprovedState)
            "
          >
            {{ appointmentApprovedStateSpelledOut(arg.event.extendedProps.appointment) }}
          </b-badge>
        </div>
        <!-- <b-badge v-if="arg.event.extendedProps.zipArea">
          PLZ&nbsp;
          {{ arg.event.extendedProps.zipArea }}
        </b-badge> -->
      </div>
      <b-badge
        class="event-employee-badge"
        variant="primary"
        v-if="hasEmployees(arg.event.extendedProps)"
      >
        {{ firstnameShortFullLastname(arg.event.extendedProps) }}
      </b-badge>
      <div v-if="arg.event.extendedProps.appointment" class="pt-1 pl-1">
        {{ $time(arg.event.extendedProps.appointment.installationStartAt) }} -
        {{ $time(arg.event.extendedProps.appointment.installationEndAt) }}
      </div>
      <div
        v-if="arg.event.extendedProps.appointment && arg.event.extendedProps.appointment.project"
        class="pl-1"
      >
        {{ arg.event.extendedProps.appointment.project.number.toString().padStart(4, '0') }},
        {{ arg.event.extendedProps.appointment.project.customer.lastname }},
        {{ arg.event.extendedProps.appointment.project.customer.firstname }}
      </div>
      <div v-if="!arg.event.extendedProps.appointment" class="pl-1 pt-1"><b>Frei</b></div>
    </template>
  </FullCalendar>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import de from '@fullcalendar/core/locales/de';
import {
  getAppointmentTypeColor,
  getAppointmentStateColor,
  getYesNoStateColor,
} from '@/helpers/colors';

export default {
  components: {
    FullCalendar,
  },
  props: {
    selectEventCallback: Function,
    dateChangedCallback: Function,
  },
  data() {
    return {
      startDate: null,
      endDate: null,

      calendarOptions: {
        displayEventTime: false,
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
        },
        locale: de,
        plugins: [timeGridPlugin, dayGridPlugin, interactionPlugin],
        initialView: 'dayGridWeek',
        selectable: true,
        events: this.appointmentCapacities,
        eventClick: this.handleEventClick,
        eventOrder: 'prio',
        customButtons: {
          customPrev: {
            text: 'Prev',
            click: this.handlePrevClick,
          },
          customNext: {
            text: 'Next',
            click: this.handleNextClick,
          },
          customToday: {
            text: 'Heute',
            click: this.handleTodayClick,
          },
          customDayGridWeek: {
            text: 'Woche',
            click: this.handleDayGridWeekClick,
          },
          customDayGridMonth: {
            text: 'Monat',
            click: this.handleDayGridMonthClick,
          },
        },
        headerToolbar: {
          left: 'customPrev,customNext,customToday',
          center: 'title',
          right: 'customDayGridWeek,customDayGridMonth',
        },
        buttonIcons: {
          customNext: 'chevron-right',
          customPrev: 'chevron-left',
        },
        weekNumbers: true,
      },
    };
  },
  computed: {
    ...mapGetters(['getAppointmentCapacities']),
    appointmentCapacities() {
      return this.getAppointmentCapacities;
    },
  },
  methods: {
    getAppointmentTypeColor,
    getAppointmentStateColor,
    getYesNoStateColor,
    hasEmployees(appointmentCapacity) {
      if (appointmentCapacity.appointment && appointmentCapacity.appointment.employees.length > 0) {
        return true;
      }

      return appointmentCapacity.employees.length > 0;
    },

    /**
     * Generate a string with the initials of the employees that are assigned to a apointmentCapacity/appointment
     * If an appointment is present in the apointmentCapacity use the employees from the appointment, else use the employees from the apointmentCapacity
     */
    employeeShortcodes(appointmentCapacity) {
      const employeeList = appointmentCapacity.appointment
        ? appointmentCapacity.appointment.employees
        : appointmentCapacity.employees;
      let employeeString = '';
      employeeString = employeeList.reduce((acc, employee, index) => {
        const space = this.isFirstCharDigit(employee.firstname) ? ' ' : '';
        const initials =
          this.nameToInitial(employee.firstname) + space + this.nameToInitial(employee.lastname);

        if (index === 0) {
          return initials ? acc + initials : acc; // Skip if both first and last names are null or undefined
        } else {
          return initials ? acc + ', ' + initials : acc; // Skip if both first and last names are null or undefined
        }
      }, employeeString);

      return employeeString;
    },

    appointmentApprovedStateSpelledOut(appointment) {
      if (!appointment || !appointment.appointmentApprovedState) {
        return this.$t('appointmentApprovedState_no');
      }

      return appointment.appointmentApprovedState === 'YES'
        ? this.$t('appointmentApprovedState_yes')
        : this.$t('appointmentApprovedState_no');
    },

    firstnameShortFullLastname(appointmentCapacity) {
      const employeeList = appointmentCapacity.appointment
        ? appointmentCapacity.appointment.employees
        : appointmentCapacity.employees;
      let employeeString = '';
      employeeString = employeeList.reduce((acc, employee, index) => {
        const firstInitial = this.nameToInitial(employee.firstname);
        const space = this.isFirstCharDigit(employee.firstname) ? ' ' : '';
        const initials = firstInitial
          ? firstInitial + '.' + space + (employee.lastname || '')
          : employee.lastname || '';

        if (index === 0) {
          return initials ? acc + initials : acc; // Skip if both first and last names are null or undefined
        } else {
          return initials ? acc + ', ' + initials : acc; // Skip if both first and last names are null or undefined
        }
      }, employeeString);

      return employeeString;
    },

    /**
     * Format a name to a single character or if numeric return as is
     * @param {*} name
     */
    nameToInitial(name) {
      if (!name) return '';
      if (this.isFirstCharDigit(name)) return name;
      return name.charAt(0);
    },

    isFirstCharDigit(str) {
      return /^[0-9]/.test(str);
    },

    handleEventClick: function(data) {
      if (this.selectEventCallback) {
        this.selectEventCallback(data.event.id);
      }
    },

    handlePrevClick() {
      this.$refs.fullCalendar.getApi().prev();
      this.updateDateInfo();
    },

    handleNextClick() {
      this.$refs.fullCalendar.getApi().next();
      this.updateDateInfo();
    },

    handleTodayClick() {
      this.$refs.fullCalendar.getApi().today();
      this.updateDateInfo();
    },

    handledayGridWeekClick() {
      this.$refs.fullCalendar.getApi().changeView('dayGridWeek');
      this.updateDateInfo();
    },

    handleDayGridWeekClick() {
      this.$refs.fullCalendar.getApi().changeView('dayGridWeek');
      this.updateDateInfo();
    },

    handleDayGridMonthClick() {
      this.$refs.fullCalendar.getApi().changeView('dayGridMonth');
      this.updateDateInfo();
    },

    updateDateInfo() {
      this.getCurrentDateRange();
      this.dateChangedCallback({ startDate: this.startDate, endDate: this.endDate });
    },

    refreshCalendar() {
      if (!this.$refs.fullCalendar) {
        return;
      }

      this.$refs.fullCalendar.getApi().render();
    },

    getCurrentDateRange() {
      if (this.$refs.fullCalendar) {
        const calendarApi = this.$refs.fullCalendar.getApi();
        const view = calendarApi.view;
        this.startDate = view.activeStart;
        this.endDate = view.activeEnd;
        // console.log('Current Date Range:', this.startDate, this.endDate);
        // Use startDate and endDate for further processing
      } else {
        console.warn('Calendar instance not yet available');
      }
    },
  },

  async mounted() {
    this.calendarOptions.events = this.appointmentCapacities;
  },

  watch: {
    appointmentCapacities(newValue, oldValue) {
      this.calendarOptions.events = newValue;
    },
  },
};
</script>

<style scoped lang="scss">
#calendar {
  width: 100%;
  margin: 0 auto;
}

::v-deep .pl-1 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
::v-deep .badge {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 0;
}

::v-deep .event-content {
  margin-bottom: 0;
}

::v-deep .event-first-line {
  display: flex;
  width: 100%;
  margin-bottom: 0;
}

::v-deep .event-badge-type {
  width: 25%;
  min-width: 0;
}

::v-deep .event-badge-state {
  width: 40%;
  min-width: 0;
}

::v-deep .event-badge-approved {
  width: 35%;
  min-width: 0;
}

::v-deep .event-employee-badge {
  margin-top: 0;
  width: 100%;
  display: block;
  box-sizing: border-box;
}
</style>
